'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart, Customized, ResponsiveContainer, YAxis } from 'recharts';
import {
  parseStrengthWorkoutStep,
  type StrengthWorkoutStep,
} from '@trainme/api/client';

type ChartStep = StrengthWorkoutStep & {
  label: string;
  totalSeconds: number;
  cumulativeSeconds: number;
};

type Props = {
  workoutSteps: string[] | undefined;
  compact?: boolean;
};

type PlotBox = { x: number; y: number; width: number; height: number };

const formatWeight = (
  value: number,
  unit: StrengthWorkoutStep['weightDisplayUnit']
) => `${value}${unit === 'POUND' ? 'lb' : 'kg'}`;

const formatRestDuration = (seconds: number) => {
  return 'rest';
};

const formatExerciseLabel = (value: string, compact: boolean) => {
  if (!compact) return value;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'bench press') return 'BP';
  if (normalized === 'chest fly') return 'CF';
  const initials = value
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 3);
  return initials.toUpperCase() || value;
};

const formatTickMinutes = (seconds: number) => String(Math.round(seconds / 60));

const StrengthRects = ({
  plot,
  data,
  maxTotalSeconds,
  maxWeight,
  fontSize,
  compact,
  axisBottomReserve,
  tickFontSize,
}: {
  plot: PlotBox;
  data: ChartStep[];
  maxTotalSeconds: number;
  maxWeight: number;
  fontSize: number;
  compact: boolean;
  axisBottomReserve: number;
  tickFontSize: number;
}) => {
  if (!data.length || maxTotalSeconds <= 0 || maxWeight <= 0) {
    return null;
  }

  const plotWidth = Math.max(1, Math.floor(plot.width));
  const topReserve = compact
    ? Math.max(6, Math.round(fontSize * 0.6))
    : Math.max(10, Math.round(fontSize * 0.9));
  const plotHeight = Math.max(
    1,
    Math.floor(plot.height - axisBottomReserve - topReserve)
  );
  const axisY = plot.y + topReserve + plotHeight;
  const columnWidth = plotWidth / data.length;
  const columnInnerWidth = Math.max(8, Math.floor(columnWidth * 0.76));
  // Font size for labels displayed directly on chart elements (e.g., set details or exercise labels in compact view).
  const labelFontSize = Math.max(
    5,
    compact ? Math.round(fontSize * 0.72) : Math.round(fontSize * 0.82)
  );
  const tickLabelY = axisY + Math.max(4, Math.round(fontSize * 0.55));
  return (
    <>
      {data.map((step, index) => {
        const setDuration = step.reps * 5 + step.restDurationSeconds;
        const columnHeight = Math.max(
          1,
          Math.round((step.totalSeconds / maxTotalSeconds) * plotHeight)
        );
        const pairHeight = Math.max(
          1,
          Math.floor(columnHeight / step.repeatCount)
        );
        const activeHeight = Math.max(
          1,
          Math.round(pairHeight * ((step.reps * 5) / setDuration))
        );
        const restHeight = Math.max(1, pairHeight - activeHeight);
        const blockWidth = Math.max(
          compact ? 0.48 : 0.56,
          Math.min(1, step.weightValue / maxWeight)
        );
        const xCenter = plot.x + columnWidth * index + columnWidth / 2;
        const barWidth = Math.max(8, Math.round(columnInnerWidth * blockWidth));
        const x = Math.round(xCenter - barWidth / 2);
        const yTop = Math.round(axisY - columnHeight);

        return (
          <g key={`${step.exerciseName}-${index}`}>
            {Array.from({ length: step.repeatCount }).map((_, repIndex) => {
              const pairTop = axisY - (repIndex + 1) * pairHeight;
              const grayY = pairTop;
              const greenY = pairTop + restHeight;

              return (
                <g key={`${step.exerciseName}-${index}-${repIndex}`}>
                  <rect
                    x={x}
                    y={grayY}
                    width={barWidth}
                    height={restHeight}
                    rx={restHeight / 2}
                    ry={restHeight / 2}
                    className={'fill-gray-300 dark:fill-gray-700'}
                    opacity={compact ? 0.95 : 0.9}
                    shapeRendering='crispEdges'
                  />
                  {!compact && (
                    <text
                      x={x + barWidth / 2}
                      y={grayY + restHeight / 2}
                      textAnchor='middle'
                      dominantBaseline='middle'
                      fontSize={labelFontSize}
                      fontWeight={600}
                      className='fill-slate-500 dark:fill-slate-300'
                    >
                      {/* Displays the formatted rest duration for each set in non-compact view */}
                      {formatRestDuration(step.restDurationSeconds)}
                    </text>
                  )}
                  <rect
                    x={x}
                    y={greenY}
                    width={barWidth}
                    height={activeHeight}
                    rx={activeHeight / 2}
                    ry={activeHeight / 2}
                    className='fill-emerald-400 dark:fill-teal-700'
                    shapeRendering='crispEdges'
                  />
                  {!compact && (
                    <text
                      x={x + barWidth / 2}
                      y={greenY + activeHeight / 2 + 1}
                      textAnchor='middle'
                      dominantBaseline='middle'
                      fontSize={Math.max(5, Math.round(labelFontSize * 0.92))}
                      fontWeight={700}
                      className='fill-slate-950 dark:fill-slate-50'
                    >
                      {/* Displays the reps and weight for each set in non-compact view */}
                      {step.reps} @{' '}
                      {formatWeight(step.weightValue, step.weightDisplayUnit)}
                    </text>
                  )}
                </g>
              );
            })}
            {compact && (
              <text
                x={xCenter}
                y={yTop + columnHeight / 2}
                textAnchor='middle'
                dominantBaseline='middle'
                fontSize={Math.max(8, Math.round(labelFontSize))}
                fontWeight={600}
                className='fill-slate-950 dark:fill-slate-100'
              >
                {/* Displays the abbreviated exercise label in compact view */}
                {step.label}
              </text>
            )}
          </g>
        );
      })}
      {data.map((step, index) => {
        const columnWidth = plotWidth / data.length;
        const x = plot.x + columnWidth * (index + 1) - 4;
        return (
          <g key={`tick-${step.exerciseName}-${index}`}>
            <line
              x1={x}
              x2={x}
              y1={axisY - 1}
              y2={axisY + 2}
              stroke='#9ca3af'
              strokeWidth={1}
            />
            <text
              x={x}
              y={tickLabelY + 4}
              textAnchor='middle'
              dominantBaseline='middle'
              // Font size for x-axis tick labels. In compact view, this `tickFontSize` already includes a +2 offset for better readability.
              fontSize={compact ? tickFontSize : fontSize}
              fontWeight={500}
              className='fill-slate-500'
            >
              {formatTickMinutes(step.cumulativeSeconds)}
            </text>
          </g>
        );
      })}
    </>
  );
};

export default function StrengthWorkoutChart({
  workoutSteps,
  compact = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setContainerWidth(rect.width);
      setContainerHeight(rect.height);
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const steps = useMemo(
    () =>
      (workoutSteps ?? [])
        .map(parseStrengthWorkoutStep)
        .filter((step): step is StrengthWorkoutStep => Boolean(step))
        .map((step) => ({
          ...step,
          label: compact
            ? formatExerciseLabel(step.exerciseName, compact)
            : step.exerciseName,
          totalSeconds:
            step.repeatCount * (step.reps * 5 + step.restDurationSeconds),
        })),
    [workoutSteps, compact]
  );

  const stepsWithCumulative = useMemo(() => {
    return steps.reduce(
      (acc: ChartStep[], step: Omit<ChartStep, 'cumulativeSeconds'>) => {
        const prevCumulativeSeconds =
          acc.length > 0 ? acc[acc.length - 1].cumulativeSeconds : 0;
        const currentCumulativeSeconds =
          prevCumulativeSeconds + step.totalSeconds;
        return [
          ...acc,
          {
            ...step,
            cumulativeSeconds: currentCumulativeSeconds,
          } as ChartStep,
        ];
      },
      []
    );
  }, [steps]);

  if (steps.length === 0) {
    return (
      <div className='flex h-full items-center justify-center rounded-lg px-4 py-8 text-sm text-gray-500 dark:text-gray-400 bg:transparent border border-dashed border-gray-300 dark:border-gray-700'>
        &nbsp;
      </div>
    );
  }

  const maxWeight = Math.max(
    ...stepsWithCumulative.map((step) => step.weightValue),
    1
  );
  const maxTotalSeconds = Math.max(
    ...stepsWithCumulative.map((step) => step.totalSeconds),
    1
  );
  // Base font size for chart elements, dynamically adjusted based on container dimensions and compact mode.
  const fontSize = compact
    ? Math.max(
        7,
        Math.min(10, Math.round(Math.max(containerWidth, containerHeight) / 24))
      )
    : Math.max(
        9, // User's preferred minimum font size for non-compact view
        Math.min(13, Math.round(Math.max(containerWidth, containerHeight) / 36))
      );
  // Font size for x-axis tick labels, adjusted for compact mode to account for additional space.
  const tickFontSize = compact
    ? Math.max(12, Math.min(Math.round(containerHeight / 6), 16)) // Incorporates a +2 offset for compact view directly
    : Math.max(8, Math.min(Math.round(containerHeight / 5), 11));
  // Reserved space at the bottom for axis labels and ticks, dynamically adjusted based on compact mode and font size.
  const axisBottomReserve = compact
    ? Math.max(12, fontSize + 2)
    : Math.max(36, fontSize + 20);
  const margin = {
    top: 0,
    right: -1,
    bottom: axisBottomReserve,
    left: 0,
  };

  // Define padding for the chart's plotting area to prevent labels from being cut off.
  const leftPaddingPx = 0; // Adjust as needed
  const rightPaddingPx = 5; // 10px padding on the right to prevent labels from being cut off.

  return (
    <div ref={containerRef} className='h-full w-full min-h-0'>
      {containerWidth > 0 && containerHeight > 0 && (
        <ResponsiveContainer
          width='100%'
          height='100%'
          minWidth={1}
          minHeight={1}
        >
          <BarChart
            data={stepsWithCumulative}
            margin={margin}
            barGap={0}
            barCategoryGap='10%'
            key={`strength-${compact ? 'compact' : 'full'}-${steps.length}`}
          >
            <Customized
              component={(p: {
                x?: number;
                y?: number;
                width?: number;
                height?: number;
              }) => {
                const effectiveWidth =
                  (p.width ?? 0) - leftPaddingPx - rightPaddingPx;
                const effectiveX = (p.x ?? 0) + leftPaddingPx;
                return (
                  <StrengthRects
                    plot={{
                      x: effectiveX,
                      y: p.y ?? 0,
                      width: effectiveWidth,
                      height: p.height ?? 0,
                    }}
                    data={stepsWithCumulative}
                    maxTotalSeconds={maxTotalSeconds}
                    maxWeight={maxWeight}
                    fontSize={fontSize}
                    compact={compact}
                    axisBottomReserve={axisBottomReserve}
                    tickFontSize={tickFontSize}
                  />
                );
              }}
            />
            <Customized
              component={(p: {
                x?: number;
                y?: number;
                width?: number;
                height?: number;
              }) => {
                const left = (p.x ?? 0) + leftPaddingPx; // Adjust x position by left padding
                const top = p.y ?? 0;
                const width = (p.width ?? 0) - leftPaddingPx - rightPaddingPx; // Adjust width for both paddings
                const height = p.height ?? 0;
                const axisY =
                  top + Math.max(1, Math.floor(height - axisBottomReserve));
                const tickY =
                  axisY +
                  (compact
                    ? Math.max(12, Math.round(fontSize * 1.35))
                    : Math.max(16, Math.round(fontSize * 1.75)));
                const axisInset = 0;
                const axisWidth = Math.max(1, width);
                return (
                  <>
                    <line
                      x1={left + axisInset}
                      x2={left + axisInset + axisWidth}
                      y1={axisY}
                      y2={axisY}
                      className='stroke-slate-500'
                      strokeWidth={1}
                    />
                    {!compact &&
                      stepsWithCumulative.map((step, index) => {
                        const columnWidth = axisWidth / steps.length;
                        const x =
                          left +
                          axisInset +
                          columnWidth * index +
                          columnWidth / 2;
                        return (
                          <text
                            key={`axis-${step.exerciseName}-${index}`}
                            x={x}
                            y={tickY}
                            textAnchor='middle'
                            dominantBaseline='middle'
                            fontSize={Math.max(
                              5,
                              compact
                                ? Math.round(fontSize * 0.64)
                                : Math.round(fontSize * 0.6)
                            )}
                            fontWeight={500}
                            fill='#9ca3af'
                          >
                            {step.label}
                          </text>
                        );
                      })}
                  </>
                );
              }}
            />
            <YAxis hide width={0} tick={false} axisLine={false} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
